/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {KeyboardEvent, ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChipInput from 'material-ui-chip-input';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor, dlsTypography} from '../../theme/theme';

export interface ChipTextFieldTypes {
  label?: string;
  onChange?: (value: string[]) => void;
  defaultValue?: string[];
  readOnly?: boolean;
  hasError?: boolean;
  errorText?: string;
  fieldLabel?: string | ReactNode;
  required?: boolean;
  placeholder?: string;
  name?: string;
  blurBehavior?: 'clear' |'add' | 'ignore',
  newChipKeys?: KeyboardEvent['key'][];
  InputProps?: GenericObject;
  FormHelperTextProps?: GenericObject;
  inputRef?: (ref: React.Ref<HTMLInputElement>) => any;
  onUpdateInput?: (event) => void;
  onBeforeAdd?: (chip: any) => boolean;
  autoComplete?: string;
}

const useStyles = makeStyles(theme => ({
  fieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },
  root : {
    borderBottom  : `solid 1px ${dlsColor('grey5')}`,
    paddingBottom : '2px'
  },
  chipContainer : {
    '&&&&' : {
      alignItems      : 'center',
      backgroundColor : dlsColor('white'),
      marginTop       : '0px',
      minHeight       : '49px',
      outline         : 'none',
      padding         : '4px 0'
    },
    '&&:before' : {
      borderBottom : 'none'
    },
    '&:hover:not(.WAMuiChipInput-disabled):not(.WAMuiChipInput-focused):not(.WAMuiChipInput-error)' : {
      '&:before' : {
        borderBottom : 'none'
      }
    }
  },
  chip : {
    borderRadius : '4px',
    margin       : '8px 4px 8px 8px',
    maxHeight    : '24px',
    '& span'     : {
      ...dlsTypography('chips')
    },
    '& svg' : {
      height : '15px',
      width  : '15px'
    }
  },
  input : {
    paddingLeft : '7px'
  },
  inputRoot : {
    outline : 'none'
  },
  helperText : {
    marginBottom : '-19px'
  },
  underline : {
    '&:after' : {
      borderBottomColor : dlsColor('caBlue'),
      bottom            : '-1px'
    }
  }
}));

/**
 * @ChipTextField
 *
 * Defines the ChipTextField component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   <ChipTextField fieldLabel='Name'
 *     required
 *     alwaysShowPlaceholder
 *     onChange={function(value) {
 *       console.log(value) // string[]
 *     }}
 *     hasError
 *     errorText='Please enter a valid Name' />
 *
 * ```
 */
export const ChipTextField = ({
  label,
  onChange,
  defaultValue,
  hasError = false,
  readOnly = false,
  fieldLabel,
  required = false,
  placeholder,
  errorText,
  name,
  blurBehavior = 'add',
  newChipKeys = ['Enter', 'Tab', 'ArrowRight'],
  InputProps = {},
  FormHelperTextProps = {},
  inputRef,
  onUpdateInput,
  autoComplete,
  ...props
}: ChipTextFieldTypes) => {
  const classes = useStyles();
  return (
    <>
      {fieldLabel &&
        <div className={classes.fieldLabel}>
          {fieldLabel}
          {required && <span className={classes.requiredAsterisk}>*</span>}
        </div>}
      <ChipInput error={hasError}
        alwaysShowPlaceholder
        blurBehavior={blurBehavior}
        classes={{
          root          : classes.root,
          chipContainer : classes.chipContainer,
          chip          : classes.chip,
          input         : classes.input,
          inputRoot     : classes.inputRoot,
          helperText    : classes.helperText,
          underline     : classes.underline
        }}
        disabled={readOnly}
        onChange={onChange}
        defaultValue={defaultValue}
        newChipKeys={newChipKeys}
        helperText={hasError && errorText}
        required={required}
        InputProps={{
          autoComplete : autoComplete,
          name,
          ...InputProps
        }}
        FormHelperTextProps={FormHelperTextProps}
        label={!placeholder && label}
        placeholder={placeholder}
        onUpdateInput={onUpdateInput}
        inputRef={inputRef}
        {...props} />
    </>
  );
};

export default ChipTextField;
